import { contactPipelineInput } from 'app/lib/contactPipelineInput'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { queueNavSearch, readLocationIds } from './locationParam'
import { ACTION_QUEUE_BY_ID, ActionQueueDef, actionPlayFor } from './queueRegistry'
import { activeSnoozes, loadSnoozes, MomentumKind, recordMomentum, snoozeContact } from './momentumStore'
import { createClosedLostMilestone } from './closedLostMilestone'
import { useQueueValueStats } from './useQueueValueStats'
import moment from 'moment'
import { CARD_CALL_QUERY, CARD_CALL_RECORDING_QUERY, UPDATE_CONTACT_MUTATION } from './graphql'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { useListCopy } from 'app/hooks/useListCopy'
import { gqlFetchList, gqlMutation, gqlQuery } from 'app/lib/graphqlClient'
import { ActivityJson, ContactJson } from 'app/types/entities'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useUser, useWorkspace } from 'app/utils'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'
import { TREATMENT_LIST, toBookStatusTerms } from 'lib/systemLists'

const PAGE_SIZE = 25

export type CardActivity = {
  activity: ActivityJson
  /** `trigger` = the activity that caused Needs Action; `last-call` = context. */
  role: `trigger` | `last-call`
  /** For Call activities: the linked Call record's substance. */
  call?: {
    callId?: string
    summary?: string
    duration?: number
    answered?: boolean
    waveforms?: string[]
    recordingUrl?: string
  }
}

export type UseFocusQueueReturn = {
  user: ReturnType<typeof useUser>
  def: ActionQueueDef | undefined
  contact: ContactJson | undefined
  cardActivity: CardActivity | undefined
  index: number
  total: number
  loading: boolean
  /** True once the flow has stepped past the last available contact. */
  done: boolean
  perItemValue?: number
  sessionWorked: number
  sessionValue: number
  drawerOpen: boolean
  openProfile: () => void
  closeProfile: () => void
  next: () => void
  prev: () => void
  recordCall: () => void
  /** Records an SMS against the session; the thread owns the actual send. */
  recordSms: () => void
  needsActionDateEnabled: boolean
  locationTerm?: string
  /** Records a created task against the session. */
  recordTask: () => void
  snooze: (days: number) => void
  submitClosedLost: (data: {
    closedLostReason?: string
    disqualified?: string
    note?: string
    owner?: { id: string; name: string }
  }) => void
  submitLeadStatus: (leadStatus: string) => void
  setPipelineStage: (pipelineId: string, stageName: string, pipelineName?: string) => void
  removeFromPipeline: (pipelineId: string, pipelineName?: string) => void
  /** Pipeline id whose save is in flight, for the rail's pending state. */
  pipelineSaving: string | undefined
  onFollowUpLogged: () => void
  applyContactChange: (contact: ContactJson) => void
  exit: () => void
}

export function useFocusQueue(queueIdArg?: string): UseFocusQueueReturn {
  const params = useParams<{ queueId: string }>()
  const queueId = queueIdArg ?? params.queueId
  const navigate = useNavigate()
  const routerLocation = useLocation()
  const [searchParams] = useSearchParams()
  const user = useUser()
  const workspace = useWorkspace()
  const needsActionDateEnabled = useFeatureToggle(`needs-action-date`)
  const groupedNavigation = !!useFeatureToggle(`grouped-navigation`)
  const copy = useListCopy()
  const def = ACTION_QUEUE_BY_ID.get(queueId)
  // Location drill-down carried over from the overview (`filters` param).
  const filtersParam = searchParams.get(`filters`)
  const locationIds = useMemo(() => readLocationIds(filtersParam), [filtersParam])
  const { stats: valueStats } = useQueueValueStats(workspace?.id, locationIds)

  const [items, setItems] = useState<ContactJson[]>([])
  const [index, setIndex] = useState(0)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [allLoaded, setAllLoaded] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const pageRef = useRef(0)
  // Generation guard: filter changes (e.g. a feature toggle hydrating after
  // mount) bump the generation so in-flight results for stale filters are
  // discarded instead of blocking the refetch.
  const generationRef = useRef(0)
  const loadingMoreRef = useRef(false)
  const sessionContactsRef = useRef(new Set<string>())
  const [sessionWorked, setSessionWorked] = useState(0)
  const [sessionValue, setSessionValue] = useState(0)

  const perItemValue = def?.perItemValue(valueStats)

  const locationTerm = locationIds.length
    ? `locationId:(${locationIds.map((id) => `"${id}"`).join(` OR `)})`
    : undefined

  const searchTerms = useMemo(() => {
    if (!def) return undefined
    const terms = def.searchTerms({ needsActionDateEnabled: !!needsActionDateEnabled })
    // The Unscheduled Treatment queue works only what is still to book, as its count does; the
    // Action Center's own treatment queues, toggle off, keep everyone.
    if (groupedNavigation && def.bundle === `treatment`) terms.push(...toBookStatusTerms(TREATMENT_LIST))
    if (locationIds.length) terms.push(`locationId:(${locationIds.map((id) => `"${id}"`).join(` OR `)})`)
    return terms.join(` `)
  }, [def, needsActionDateEnabled, groupedNavigation, locationIds])

  const loadPage = useCallback(
    (page: number, replace: boolean) => {
      if (!def || !user?.id || !searchTerms) return
      if (!replace && loadingMoreRef.current) return
      const generation = replace ? ++generationRef.current : generationRef.current
      if (replace) setLoading(true)
      else loadingMoreRef.current = true
      // Snoozes are per-queue: a lead snoozed in Active Leads shouldn't shrink
      // (or hide from) the hygiene queue. They are not per-user: a contact
      // somebody set aside is set aside for whoever picks the queue up next.
      const snoozedIds = new Set(
        activeSnoozes(workspace?.id ?? ``)
          .filter((s) => s.queueId === def.id)
          .map((s) => s.contactId),
      )
      bg(
        gqlFetchList(`contacts`, {
          search: searchTerms,
          pagination: { page, limit: PAGE_SIZE },
          sort: def.sort({ needsActionDateEnabled: !!needsActionDateEnabled }),
        })
          .then(({ hits, total: serverTotal }) => {
            if (generation !== generationRef.current) return
            const fresh = hits.filter((hit) => hit.id && !snoozedIds.has(hit.id))
            setItems((prev) => (replace ? fresh : [...prev, ...fresh]))
            setTotal(Math.max(0, serverTotal - snoozedIds.size))
            setAllLoaded((page + 1) * PAGE_SIZE >= serverTotal)
            pageRef.current = page
          })
          .catch((err) => {
            logError(`Error fetching Action Center queue`, err)
            notifyError(`Couldn't load the queue. Please try again.`)
          })
          .finally(() => {
            if (!replace) loadingMoreRef.current = false
            if (generation === generationRef.current) setLoading(false)
          }),
      )
    },
    [def, user?.id, searchTerms, needsActionDateEnabled],
  )

  // The workspace's snoozes come off the API now, and the first page is only
  // fetched once they are known: filtering them out afterwards would deal the
  // flow a card and then snatch it back. Deliberately not re-keyed on later
  // snooze changes: the flow removes its own snoozes locally, and reloading
  // on each would throw the person back to the first card.
  const [snoozesReady, setSnoozesReady] = useState(false)
  useEffect(() => {
    if (!workspace?.id) return
    let cancelled = false
    bg(loadSnoozes(workspace.id).then(() => !cancelled && setSnoozesReady(true)))
    return () => {
      cancelled = true
    }
  }, [workspace?.id])

  // Initial load + reload when the queue definition changes.
  useEffect(() => {
    if (!def || !snoozesReady) return
    setItems([])
    setIndex(0)
    setAllLoaded(false)
    loadPage(0, true)
  }, [def?.id, searchTerms, snoozesReady])

  // Prefetch the next page as the flow nears the end of the loaded window.
  useEffect(() => {
    if (loading || allLoaded) return
    if (items.length - index <= 5) loadPage(pageRef.current + 1, false)
  }, [index, items.length, loading, allLoaded, loadPage])

  const contact = items[index]
  const done = !loading && (items.length === 0 || index >= items.length) && allLoaded

  // The evidence block's activity. For activity-triggered plays ("New Form",
  // "Missed Call") it's the triggering activity; for everything else it falls
  // back to the latest Call activity as "last call" context — the most recent
  // real conversation is context gold on any card. Cached per contact.
  const [cardActivity, setCardActivity] = useState<CardActivity | undefined>()
  const cardActivityCacheRef = useRef(new Map<string, CardActivity | null>())
  const currentContactIdRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    currentContactIdRef.current = contact?.id
    setCardActivity(undefined)
    if (!contact?.id || !def) return
    const triggerTypes = actionPlayFor(def.id, contact).triggerActivityTypes
    const role: CardActivity[`role`] = triggerTypes?.length ? `trigger` : `last-call`
    const types = triggerTypes?.length ? triggerTypes : [`Call`]
    const contactId = contact.id
    const cached = cardActivityCacheRef.current.get(contactId)
    if (cached !== undefined) {
      setCardActivity(cached ?? undefined)
      return
    }
    bg(
      gqlFetchList<ActivityJson>(`activities`, {
        search: `workspaceId:${contact.workspaceId} locationId:${contact.locationId} contacts.id.keyword:${contactId}`,
        sort: { field: `activityDate`, direction: `DESC` },
        pagination: { limit: 10 },
      })
        .then(async ({ hits }) => {
          const match = hits.find((activity) => activity.type && types.includes(activity.type))
          let result: CardActivity | null = match ? { activity: match, role } : null
          // A Call activity's row only carries boilerplate — the summary and
          // recording live on the linked Call record. Recording URLs aren't
          // requested for calls older than two years (mirrors the drawer).
          if (result && match?.type === `Call` && match.typeId) {
            const tooOldForRecording = moment().diff(moment(match.activityDate), `months`) > 24
            const [callResult, recordingResult] = await Promise.all([
              gqlQuery<{
                call?: {
                  id?: string
                  answered?: boolean
                  duration?: number
                  waveforms?: string[]
                  ai?: { summary?: string }
                }
              }>(CARD_CALL_QUERY, { id: match.typeId }),
              tooOldForRecording
                ? Promise.resolve(undefined)
                : gqlQuery<{ callRecordingUrl?: string }>(CARD_CALL_RECORDING_QUERY, { id: match.typeId }),
            ])
            const call = callResult.data?.call
            if (call) {
              result = {
                ...result,
                call: {
                  callId: call.id,
                  summary: call.ai?.summary ?? undefined,
                  duration: call.duration,
                  answered: call.answered,
                  waveforms: call.waveforms ?? undefined,
                  recordingUrl: recordingResult?.data?.callRecordingUrl ?? undefined,
                },
              }
            }
          }
          cardActivityCacheRef.current.set(contactId, result)
          if (currentContactIdRef.current === contactId) setCardActivity(result ?? undefined)
        })
        .catch((err) => logError(`Error fetching card activity`, err)),
    )
  }, [contact?.id, def])

  const track = useCallback(
    (kind: MomentumKind, target: ContactJson) => {
      if (!user?.id || !def || !target.id) return
      recordMomentum(user.id, { queueId: def.id, contactId: target.id, kind, estValue: perItemValue })
      if (!sessionContactsRef.current.has(target.id)) {
        sessionContactsRef.current.add(target.id)
        setSessionWorked((prev) => prev + 1)
        if (perItemValue) setSessionValue((prev) => prev + perItemValue)
      }
    },
    // oxlint-disable-next-line react/preserve-manual-memoization -- existing manual memoization is intentional here
    [user?.id, def, perItemValue],
  )

  const removeCurrent = useCallback(() => {
    setItems((prev) => prev.filter((_, i) => i !== index))
    setTotal((prev) => Math.max(0, prev - 1))
    // oxlint-disable-next-line react/preserve-manual-memoization -- existing manual memoization is intentional here
  }, [index])

  const next = useCallback(() => {
    setIndex((prev) => Math.min(prev + 1, items.length))
    // oxlint-disable-next-line react/preserve-manual-memoization -- existing manual memoization is intentional here
  }, [items.length])

  const prev = useCallback(() => {
    setIndex((current) => Math.max(0, current - 1))
    // oxlint-disable-next-line react/preserve-manual-memoization -- existing manual memoization is intentional here
  }, [])

  const recordCall = useCallback(() => {
    if (contact) track(`call`, contact)
  }, [contact, track])

  /**
   * The conversation face owns the send (it needs the optimistic bubble and the
   * failed state), so this only records the outcome against the session. Sending
   * deliberately does not advance the queue any more: you stay in the thread you
   * just wrote to instead of being thrown at the next contact mid-conversation.
   */
  const recordSms = useCallback(() => {
    if (contact) track(`sms`, contact)
  }, [contact, track])

  const snooze = useCallback(
    (days: number) => {
      if (!contact?.id || !workspace?.id || !def) return
      const until = new Date()
      until.setDate(until.getDate() + days)
      snoozeContact(workspace.id, contact.id, def.id, until, user?.id)
      track(`snooze`, contact)
      removeCurrent()
      notifySuccess(`Snoozed for ${days} ${days === 1 ? `day` : `days`}`)
    },
    // oxlint-disable-next-line react/preserve-manual-memoization -- existing manual memoization is intentional here
    [contact, workspace?.id, user?.id, def, track, removeCurrent],
  )

  // Mirrors the contact drawer's closed-lost flow: the ClosedLostForm modal
  // collects reason/owner/note, and saving creates the Closed Lost milestone —
  // the backend flips the contact's leadStatus off the milestone.
  const submitClosedLost = useCallback(
    (data: {
      closedLostReason?: string
      disqualified?: string
      note?: string
      owner?: { id: string; name: string }
    }) => {
      if (!contact?.id || !user) return
      const target = { ...contact, id: contact.id }
      const closedLostReason = data.closedLostReason
      if (!closedLostReason) return
      bg(async () => {
        try {
          // The one place that decides what a Closed Lost milestone looks
          // like, see closedLostMilestone.ts. The board's drag-to-Closed-Lost
          // gate calls the same function, so the two never drift apart.
          await createClosedLostMilestone(target.id!, user.orgId, { ...data, closedLostReason })
        } catch (err) {
          logError(`Error saving closed lost from Action Center`, err)
          notifyError(`Couldn't close that lead. Nothing was saved.`)
          return
        }
        track(`closed-lost`, target)
        removeCurrent()
        notifySuccess(`Closed ${target.name ?? `this lead`}`)
      })
    },
    [contact, user, track, removeCurrent],
  )

  // Pipeline management: move a stage (or join a pipeline) with the same
  // contact update the kanban uses. Optimistic — the rail repaints instantly
  // and rolls back if the save fails, since the contact mutation is slow
  // enough that waiting for it feels broken.
  const [pipelineSaving, setPipelineSaving] = useState<string | undefined>(undefined)

  const setPipelineStage = useCallback(
    (pipelineId: string, stageName: string, pipelineName?: string) => {
      if (!contact?.id) return
      const target = { ...contact, id: contact.id }
      const previous = target.pipelines ?? []
      const memberships = [...previous]
      const index = memberships.findIndex((p) => p.id === pipelineId)
      if (index >= 0) memberships.splice(index, 1, { ...memberships[index], stage: stageName })
      else memberships.push({ id: pipelineId, stage: stageName })
      // oxlint-disable-next-line react/immutability -- existing closure or mutable API pattern is intentional
      const input = contactPipelineInput(memberships)
      if (!input) {
        notifyError(`An existing pipeline has no stage. Select its stage before saving.`)
        return
      }
      // oxlint-disable-next-line react/immutability -- existing closure mutates the contact state
      applyContactChange({ ...target, pipelines: memberships } as ContactJson)
      setPipelineSaving(pipelineId)
      bg(async () => {
        const result = await gqlMutation(UPDATE_CONTACT_MUTATION, { id: target.id, input: { pipelines: input } })
        setPipelineSaving(undefined)
        if (result.error) {
          applyContactChange({ ...target, pipelines: previous } as ContactJson)
          logError(`Error updating pipeline from Action Center`, result.error.message)
          notifyError(copy.couldNotUpdateList)
          return
        }
        track(`pipeline`, target)
        if (index < 0) notifySuccess(`Added to ${pipelineName ?? copy.noun} · ${stageName}`)
      })
    },
    [contact, track, copy],
  )

  const removeFromPipeline = useCallback(
    (pipelineId: string, pipelineName?: string) => {
      if (!contact?.id) return
      const target = { ...contact, id: contact.id }
      const previous = target.pipelines ?? []
      const memberships = previous.filter((p) => p.id !== pipelineId)
      const input = contactPipelineInput(memberships)
      if (!input) {
        notifyError(`An existing pipeline has no stage. Select its stage before saving.`)
        return
      }
      // oxlint-disable-next-line react/immutability -- existing closure mutates the contact state
      applyContactChange({ ...target, pipelines: memberships } as ContactJson)
      setPipelineSaving(pipelineId)
      bg(async () => {
        const result = await gqlMutation(UPDATE_CONTACT_MUTATION, { id: target.id, input: { pipelines: input } })
        setPipelineSaving(undefined)
        if (result.error) {
          applyContactChange({ ...target, pipelines: previous } as ContactJson)
          logError(`Error removing contact from pipeline`, result.error.message)
          notifyError(copy.couldNotUpdateList)
          return
        }
        track(`pipeline`, target)
        notifySuccess(`Removed from ${pipelineName ?? copy.noun}`)
      })
    },
    [contact, track, copy],
  )

  // The lead-status workflow: setting a status that keeps the contact in the
  // queue (Needs Action / In Progress) updates the card in place; a status
  // that graduates them out (Scheduled) removes the card and advances.
  // Closed Lost goes through submitClosedLost instead (reason modal).
  const submitLeadStatus = useCallback(
    (leadStatus: string) => {
      if (!contact?.id || !def) return
      const target = { ...contact, id: contact.id }
      bg(async () => {
        const result = await gqlMutation(UPDATE_CONTACT_MUTATION, { id: target.id, input: { leadStatus } })
        if (result.error) {
          logError(`Error updating lead status from Action Center`, result.error.message)
          notifyError(`Error updating lead status`)
          return
        }
        track(`status`, target)
        const staysInQueue = [`Needs Action`, `In Progress`].includes(leadStatus)
        if (staysInQueue) {
          applyContactChange({ ...target, leadStatus } as ContactJson)
          notifySuccess(`Status updated`)
        } else {
          removeCurrent()
          notifySuccess(leadStatus === `Scheduled` ? `Marked scheduled 🎉` : `Status updated`)
        }
      })
    },
    [contact, def, track, removeCurrent],
  )

  /**
   * Records a task against the session. The tasks panel owns the list and its
   * refresh, and creating a task no longer advances the queue: you stay on the
   * contact you were just planning work for.
   */
  const recordTask = useCallback(() => {
    if (contact) track(`task`, contact)
  }, [contact, track])

  const onFollowUpLogged = useCallback(() => {
    if (!contact) return
    track(`followup`, contact)
    next()
  }, [contact, track, next])

  // oxlint-disable-next-line react/preserve-manual-memoization -- existing manual memoization is intentional here
  const applyContactChange = useCallback((updated: ContactJson) => {
    setItems((prev) => prev.map((item) => (item.id === updated.id ? { ...item, ...updated } : item)))
  }, [])

  // Preserve the location drill-down when returning to the overview — but not
  // `wId` (see queueNavSearch: re-writing a stale one triggers a workspace
  // switch + full reload).
  const exit = useCallback(
    () => navigate({ pathname: `/action-center`, search: queueNavSearch(routerLocation.search) }),
    [navigate, routerLocation.search],
  )

  // Unknown queue id → back to the overview.
  useEffect(() => {
    if (queueId && !def) exit()
  }, [queueId, def, exit])

  return {
    user,
    def,
    contact,
    cardActivity,
    index,
    total,
    loading,
    done,
    perItemValue,
    sessionWorked,
    sessionValue,
    drawerOpen,
    openProfile: () => setDrawerOpen(true),
    closeProfile: () => setDrawerOpen(false),
    next,
    prev,
    recordCall,
    recordSms,
    needsActionDateEnabled: !!needsActionDateEnabled,
    locationTerm,
    recordTask,
    snooze,
    submitClosedLost,
    submitLeadStatus,
    setPipelineStage,
    removeFromPipeline,
    pipelineSaving,
    onFollowUpLogged,
    applyContactChange,
    exit,
  }
}
